import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import classNames from 'classnames';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import MultiButtonConfirmationModal from './multi-button-confirmation-modal';
import { GreenroomState as GS } from '../../../stores/types/types';
import { setModalState } from '../../../stores/actions/modal';
import useBroadcastState from '../../broadcasting/hooks/useBroadcastState';
import {
	ACTIVE_STATUSES,
	StreamStatus,
	CUSTOM_DESTINATION_OPTIONS as DESTINATION_OPTIONS,
	DESTINATION_TYPE,
	DestinationStatus,
} from '../../../stores/types/broadcasting';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import useScrollListOverflow from '../../../hooks/use-scroll-list-overflow';

const DestinationRow = ({ destination }: { destination: DestinationStatus }) => {
	const {
		id,
		name,
		type,
		logo,
		status,
	} = destination;

	const logoImage = useMemo(() => {
		const destType = DESTINATION_OPTIONS.find((o) => o.value === type) || DESTINATION_OPTIONS[0];
		if (type !== DESTINATION_TYPE.OTHER) return destType.description;
		return logo || destType.description;
	}, [logo, type]);

	return (
		<div className="error-item destination-error" key={id}>
			<div className="flex-center">
				<div className="logo">
					<img src={logoImage} alt={logoImage} />
				</div>
				<span className="bl-font-s1 bl-grayscale-200">
					{!id ? 'Brandlive' : name}
				</span>
			</div>
			<div className="flex-center">
				{status === StreamStatus.failed ? (
					<div className="error-icon">
						<Icon name={ICONS.ERROR} color={COLORS.WHITE} size={8} />
					</div>
				) : (
					<Icon name={ICONS.CHECK_PROCESSING} color={COLORS.PALE_GREEN} size={20} />
				)}
			</div>
		</div>
	);
};

const DestinationFailed = () => {
	const dispatch = useDispatch();

	const destinationStatus = useSelector((s: GS) => s.BroadcastReducer.destinationStatus);
	const { use_spielberg: useSpielberg } = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const [stoppingBroadcast, setStoppingBroadcast] = useState<boolean>(false);

	const { stopBroadcast, broadcastStatus } = useBroadcastState();
	const {
		showTop,
		showBottom,
		showBoth,
		scrollList,
	} = useScrollListOverflow();

	function handleCloseModal() {
		dispatch(setModalState({ modalOpen: false }));
	}

	function handleStopBroadcast() {
		setStoppingBroadcast(true);
		stopBroadcast();
	}

	useEffect(() => {
		if (useSpielberg && !ACTIVE_STATUSES.includes(broadcastStatus)) {
			handleCloseModal();
		}
	}, [useSpielberg, broadcastStatus]);

	return (
		<MultiButtonConfirmationModal
			key="destination-failed"
			firstButtonText="Continue with failed destinations"
			firstButtonAction={handleCloseModal}
			secondButtonText="Stop stream"
			secondButtonAction={handleStopBroadcast}
			secondButtonClassName={stoppingBroadcast || !ACTIVE_STATUSES.includes(broadcastStatus) ? 'disabled' : ''}
			className="confirmation-modal confirm-error error-list"
			isOpen
			buttonAlignment="right"
			firstButtonNoBackground={true}
			buttonAutoWidth={false}
			customHeader={(
				<div className="modal-custom-header-container-error">
					<h4 className="bl-font-header semantic-red-400">
						Stream Error
					</h4>
					<div className="bl-font-s3 grayscale-300">
						Your stream has started, however one or more destinations have failed to start.
					</div>
				</div>
			)}
		>
			<div
				className={classNames('error-scroll', {
					'error-scroll--top': showTop || showBoth,
					'error-scroll--bottom': showBottom || showBoth,
				})}
				ref={scrollList}
			>
				<div className="error-container">
					{destinationStatus.map(
						(destination) => <DestinationRow destination={destination} key={destination.id}/>,
					)}
				</div>
			</div>
		</MultiButtonConfirmationModal>
	);
};

export default DestinationFailed;
